"use client"

// src/components/molecules/ChatWidget.jsx
import { useEffect, useState, useRef } from "react"
import { io } from "socket.io-client"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"

export default function ChatWidget({ orderId }) {
  const { user } = useAuth()
  const [messages, setMessages] = useState([])
  const [text, setText] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const bottomRef = useRef(null)
  const socketRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    // 1) load existing chat history
    setIsLoading(true)
    api
      .get(`/chat/${orderId}`)
      .then((res) => {
        setMessages(res.data)
        bottomRef.current?.scrollIntoView({ behavior: "smooth" })
        setIsLoading(false)
      })
      .catch((error) => {
        console.error(error)
        setIsLoading(false)
      })

    // 2) open socket.io connection
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket"],
      withCredentials: true,
    })
    socketRef.current = socket

    // join our order-specific room
    socket.emit("joinOrderRoom", orderId)

    // append incoming messages
    socket.on("newMessage", (msg) => {
      setMessages((ms) => [...ms, msg])
      bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    })

    return () => {
      socket.disconnect()
    }
  }, [orderId])

  const send = async () => {
    if (!text.trim()) return
    try {
      // POST to your backend, which will broadcast back via socket
      await api.post(`/chat/${orderId}/message`, { text })
      setText("")
      inputRef.current?.focus()
    } catch (err) {
      console.error("Send message error:", err)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  return (
    <div className="border rounded-lg shadow-md bg-white max-w-lg mx-auto overflow-hidden">
      <div className="bg-amber-600 text-white p-3 font-medium flex items-center justify-between">
        <div className="flex items-center">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            ></path>
          </svg>
          Order Chat
        </div>
        <div className="text-sm opacity-80">Order #{orderId}</div>
      </div>

      <div className="h-80 overflow-y-auto p-4 bg-gray-50">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <svg
              className="w-12 h-12 mb-2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              ></path>
            </svg>
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation!</p>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`mb-3 max-w-[80%] ${m.sender === user._id ? "ml-auto" : "mr-auto"}`}>
              <div
                className={`rounded-lg p-3 ${
                  m.sender === user._id
                    ? "bg-amber-600 text-white rounded-br-none"
                    : "bg-white border border-gray-200 rounded-bl-none"
                }`}
              >
                {m.text}
              </div>
              <div className={`text-xs mt-1 text-gray-500 ${m.sender === user._id ? "text-right" : "text-left"}`}>
                {m.sender === user._id ? "You" : m.senderName || "Them"} •{" "}
                {new Date(m.timestamp || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <div className="p-3 border-t">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message…"
            rows={1}
          />
          <button
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors duration-200 flex items-center justify-center"
            onClick={send}
            disabled={!text.trim()}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              ></path>
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">Press Enter to send, Shift+Enter for a new line</p>
      </div>
    </div>
  )
}
